import { useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Play, Search, Square } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@meno/ui/components/ui/dialog'
import { Input } from '@meno/ui/components/ui/input'
import { TRANSITIONS, DEFAULT_TRANSITION_KEY } from '@/lib/transitions'
import {
  TransitionPreviewStyles, TransitionPreview, SPEED_PREVIEW_DURATION,
} from '@/components/TransitionPreview'

// Transition picker modal — a visual grid of every transition with a live
// animated preview, mirroring the Transitions page. Single-select: clicking a
// card picks that transition and closes. The Default (Cut) baseline is first and
// badged; the currently-selected transition (null → Default) keeps a ring.
export default function TransitionPickerDialog({
  open, selected, onSelect, onClose,
}: {
  open: boolean
  // The current transition key, or null when none is set (→ Default baseline).
  selected: string | null
  onSelect: (key: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  // Previews always animate on hover; Play loops them all at once so the
  // operator can compare without hovering each card. On by default here.
  const [playing, setPlaying] = useState(true)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return TRANSITIONS
    return TRANSITIONS.filter(tr =>
      tr.name.toLowerCase().includes(q) || tr.category.toLowerCase().includes(q))
  }, [search])

  // null (no explicit pick) reads as the Default baseline.
  const effectiveSelected = selected ?? DEFAULT_TRANSITION_KEY

  const pick = (key: string) => { onSelect(key); onClose() }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent
        className="max-w-3xl flex flex-col max-h-[88vh]"
        style={{ '--tr-dur': SPEED_PREVIEW_DURATION.normal } as CSSProperties}
      >
        <TransitionPreviewStyles />
        <DialogHeader>
          <DialogTitle>{t('companions.transition', 'Transition')}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('transitions.search', 'Search transitions…')}
              className="ps-9 h-9"
            />
          </div>
          <button
            type="button"
            onClick={() => setPlaying(p => !p)}
            aria-pressed={playing}
            className={`h-9 px-3 rounded-lg border text-sm font-medium transition-colors shrink-0 inline-flex items-center gap-2 ${
              playing ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            {playing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {playing ? t('transitions.stop', 'Stop') : t('transitions.play', 'Play')}
          </button>
        </div>

        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto pr-1 ${playing ? 'tr-playing' : ''}`}>
          {filtered.map(tr => {
            const isDefault = tr.key === DEFAULT_TRANSITION_KEY
            const isSelected = tr.key === effectiveSelected
            return (
              <button
                key={tr.key}
                type="button"
                onClick={() => pick(tr.key)}
                aria-pressed={isSelected}
                className={`group text-left rounded-xl overflow-hidden border-2 transition-all ${
                  isSelected
                    ? 'border-primary ring-2 ring-primary/40'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <div className="relative">
                  <TransitionPreview anim={tr.anim} className="h-20" logoClass="h-6" />
                  {isDefault && (
                    <span className="absolute top-1.5 start-1.5 z-20 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                      {t('transitions.default', 'Default')}
                    </span>
                  )}
                  {isSelected && (
                    <span className="absolute top-1.5 end-1.5 z-20 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground">
                      <Check className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <div className="px-2.5 py-2">
                  <p className="text-xs font-medium truncate">{tr.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{tr.category}</p>
                </div>
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
